import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { toast } from 'react-toastify';
import './Auth.css';

const ResetPassword = () => {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  
  const { password, confirmPassword } = formData;

  useEffect(() => {
    // Extract action code (oobCode) from URL
    const queryParams = new URLSearchParams(location.search);
    const oobCode = queryParams.get('oobCode') || token;
    
    if (!oobCode) {
      setVerificationError('Invalid or expired password reset link');
      setVerifying(false);
      return;
    }
    
    setActionCode(oobCode);
    
    // Verify the password reset code
    const verifyCode = async () => {
      try {
        // Verify the password reset code is valid
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmail(email);
        setVerifying(false);
      } catch (error) {
        console.error('Error verifying reset code:', error);
        setVerificationError('Invalid or expired password reset link');
        setVerifying(false);
      }
    };
    
    verifyCode();
  }, [location, token, auth]);
  
  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!password || !confirmPassword) {
      toast.error('Please enter all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      setLoading(true);
      
      // Complete the password reset process
      await confirmPasswordReset(auth, actionCode, password);
      
      toast.success('Password has been reset successfully');
      navigate('/login');
    } catch (error) {
      console.error('Error resetting password:', error);
      let errorMessage = 'Password reset failed';
      
      switch (error.code) {
        case 'auth/expired-action-code':
          errorMessage = 'The password reset link has expired';
          break;
        case 'auth/invalid-action-code':
          errorMessage = 'The password reset link is invalid';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This user account has been disabled';
          break;
        case 'auth/user-not-found':
          errorMessage = 'User not found';
          break;
        case 'auth/weak-password':
          errorMessage = 'The password is too weak. Please choose a stronger password';
          break;
        default:
          errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setLoading(false);
    }
  };
  
  if (verifying) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Verifying Reset Link</h1>
            <p>Please wait while we verify your password reset link...</p>
          </div>
          <div className="loading-indicator">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (verificationError) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Link Invalid</h1>
            <p>{verificationError}</p>
          </div>
          <div className="auth-footer">
            <p>
              <Link to="/forgot-password">Request a new password reset link</Link>
            </p>
            <p>
              Remember your password? <Link to="/login">Login</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Set New Password</h1>
          <p>Create a new password for {email}</p>
        </div>
        
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter new password"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
              className="form-control"
              placeholder="Confirm new password"
              required
            />
          </div>
          
          <button
            type="submit"
            className="btn"
            disabled={loading}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            Remember your password? <Link to="/login">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
